import {DateTime} from 'luxon';
import React from 'react';
import styled from 'styled-components';

import {TimeFormatsEnum} from '../../../../libs/shared/core/src/helpers/datetime/dateTimeHelpers';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {BusinessHour} from '../../../../libs/shared/core/src/models/api/businessHoursModel';
import {CalendarWeekdayToShortName} from '../../../../srcCommon/helpers/calendarHelpers';
import {TimeComboBox} from '../../../components/common/dateTime/timeComboBox';
import {CalendarWeekDaysEnum, CalendarWeekDaysShortEnum} from '../../../models/api/calendarCommonTypes';

/*
  Props.
*/

interface BusinessHoursDialogWeekdayHoursProps {
  hours: BusinessHour;
  weekday: CalendarWeekDaysEnum;
  timeFormat: TimeFormatsEnum;
  hasOvernight?: boolean;
  onSelect: (weekday: CalendarWeekDaysShortEnum, businessHours: BusinessHour) => void;
}

/*
  Style.
*/

const WrapDiv = styled.div`
  display: flex;
  align-items: center;
  margin-top: 10px;
  gap: 12px;
`;

const LabelDiv = styled.div`
  width: 76px;
  text-transform: capitalize;
  color: ${(p) => p.theme.greys.shade90};
`;

const TimeDiv = styled.div`
  width: 78px;
`;

const OvernightDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
`;

/*
  Component.
*/

export const BusinessHoursDialogWeekdayHours = ({
  weekday,
  hours,
  timeFormat,
  onSelect,
  hasOvernight = false,
}: BusinessHoursDialogWeekdayHoursProps) => {
  const selectedOpenTime = DateTime.fromISO(hours.opens);
  const selectedCloseTime = DateTime.fromISO(hours.closes);

  const onTimeSelect = (newHours: BusinessHour) => {
    onSelect(CalendarWeekdayToShortName[weekday], newHours);
  };

  const onOpenTimeSelect = (date: DateTime | undefined) => {
    if (!date) {
      return;
    }

    onTimeSelect({opens: date.toFormat('HH:mm'), closes: hours.closes});
  };

  const onCloseTimeSelect = (date: DateTime | undefined) => {
    if (!date) {
      return;
    }

    onTimeSelect({opens: hours.opens, closes: date.toFormat('HH:mm')});
  };

  const isOvernight = convertToMinutes(selectedCloseTime) <= convertToMinutes(selectedOpenTime);

  return (
    <WrapDiv data-testid={`${weekday}-hours`}>
      <LabelDiv>{weekday}</LabelDiv>
      <TimeDiv>
        <TimeComboBox
          selectedTime={selectedOpenTime}
          timeFormat={timeFormat}
          onTimeSelect={onOpenTimeSelect}
          maxTime={hasOvernight ? undefined : selectedCloseTime}
          key={`open-time-combo-${selectedOpenTime.valueOf()}`}
          noMargin
        />
      </TimeDiv>
      <TimeDiv>
        <TimeComboBox
          selectedTime={selectedCloseTime}
          timeFormat={timeFormat}
          onTimeSelect={onCloseTimeSelect}
          minTime={selectedOpenTime}
          key={`close-time-combo-${selectedCloseTime.valueOf()}`}
          noMargin
          displayOvernight={hasOvernight}
        />
      </TimeDiv>
      {hasOvernight && isOvernight ? (
        <OvernightDiv>
          <Trans id="business-hours-dialog-weekday-hours-overnight">ends the next day</Trans>
        </OvernightDiv>
      ) : null}
    </WrapDiv>
  );
};

/*
 * Helpers.
 */

function convertToMinutes(dateTime: DateTime) {
  return dateTime.get('hour') * 60 + dateTime.get('minute');
}
